/**
 * AddressMapPicker
 * ----------------
 * Picks a lat/lng from a map. Uses Google Maps JS when
 * `REACT_APP_GOOGLE_MAPS_API_KEY` is defined at build time, and gracefully
 * falls back to manual lat/lng inputs (plus a Google Maps deep-link) when
 * the key is absent — so the UI still works before the client ships a key.
 */
import { useEffect, useRef, useState } from 'react';
import { MapPin, Loader2, Target } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';

const GOOGLE_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Dubai centre as a sensible default
const DEFAULT_CENTER = { lat: 25.2048, lng: 55.2708 };

let gmapsLoader = null;
function loadGoogleMaps() {
  if (!GOOGLE_KEY) return Promise.reject(new Error('no_key'));
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (gmapsLoader) return gmapsLoader;
  gmapsLoader = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_KEY}&libraries=places&language=ar`;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve(window.google.maps);
    s.onerror = () => reject(new Error('load_failed'));
    document.head.appendChild(s);
  });
  return gmapsLoader;
}

export default function AddressMapPicker({ isAr, isDarkMode, lat, lng, onChange }) {
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const mapInstance = useRef(null);
  const [mapsAvailable, setMapsAvailable] = useState(!!GOOGLE_KEY);
  const [initializing, setInitializing] = useState(!!GOOGLE_KEY);

  useEffect(() => {
    if (!GOOGLE_KEY) {
      setMapsAvailable(false);
      return;
    }
    let cancelled = false;
    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !mapRef.current) return;
        const center = (lat && lng) ? { lat, lng } : DEFAULT_CENTER;
        mapInstance.current = new maps.Map(mapRef.current, {
          center,
          zoom: 14,
          disableDefaultUI: true,
          zoomControl: true,
          styles: isDarkMode ? DARK_MAP_STYLES : undefined,
        });
        markerRef.current = new maps.Marker({
          position: center,
          map: mapInstance.current,
          draggable: true,
        });
        markerRef.current.addListener('dragend', (e) => {
          const pos = e.latLng;
          onChange?.({ lat: pos.lat(), lng: pos.lng() });
        });
        mapInstance.current.addListener('click', (e) => {
          const pos = e.latLng;
          markerRef.current.setPosition(pos);
          onChange?.({ lat: pos.lat(), lng: pos.lng() });
        });
        setInitializing(false);
      })
      .catch(() => { if (!cancelled) { setMapsAvailable(false); setInitializing(false); } });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (markerRef.current && mapInstance.current && lat && lng) {
      const current = markerRef.current.getPosition();
      if (!current || current.lat() !== lat || current.lng() !== lng) {
        const pos = new window.google.maps.LatLng(lat, lng);
        markerRef.current.setPosition(pos);
        mapInstance.current.setCenter(pos);
      }
    }
  }, [lat, lng]);

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (p) => {
        const { latitude, longitude } = p.coords;
        onChange?.({ lat: latitude, lng: longitude });
      },
      () => {},
      { timeout: 10000 },
    );
  };

  const labelCls = isDarkMode ? 'text-gray-300' : 'text-gray-700';
  const inputCls = isDarkMode ? 'bg-[#0a0a0a] border-white/10 text-white' : 'bg-white border-gray-200';

  return (
    <div data-testid="address-map-picker" className="space-y-2">
      <div className="flex items-center justify-between">
        <Label className={labelCls}>
          {isAr ? 'الموقع على الخريطة' : 'Pin on map'}
        </Label>
        <Button type="button" size="sm" variant="outline" onClick={useMyLocation} data-testid="use-my-location-btn" className={isDarkMode ? 'border-white/10 text-gray-300' : ''}>
          <Target className="h-3.5 w-3.5 me-1" />
          {isAr ? 'موقعي الحالي' : 'My location'}
        </Button>
      </div>
      {mapsAvailable ? (
        <div className={`relative w-full h-56 rounded-xl overflow-hidden border ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
          {initializing && (
            <div className={`absolute inset-0 flex items-center justify-center ${isDarkMode ? 'bg-[#0a0a0a]/80' : 'bg-white/60'}`}>
              <Loader2 className="h-5 w-5 animate-spin text-[#1E9BD9]" />
            </div>
          )}
          <div ref={mapRef} className="w-full h-full" />
        </div>
      ) : (
        <div className={`rounded-xl border p-4 space-y-3 ${isDarkMode ? 'border-white/10 bg-[#0a0a0a]' : 'border-gray-200 bg-gray-50'}`}>
          <div className={`flex items-start gap-2 text-xs ${isDarkMode ? 'text-amber-400' : 'text-amber-600'}`}>
            <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              {isAr
                ? 'سيُفعَّل تحديد الموقع على خريطة Google فور إضافة المفتاح من الإدارة. حالياً يمكنك إدخال الإحداثيات يدوياً أو استخدام زر "موقعي الحالي".'
                : 'Google Maps picker will activate once the admin provides a key. Meanwhile, enter coordinates manually or use "My location".'}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className={labelCls}>Latitude</Label>
              <Input type="number" step="any" value={lat ?? ''} onChange={e => onChange?.({ lat: parseFloat(e.target.value) || null, lng })} className={`mt-1 ${inputCls}`} data-testid="map-lat-input" />
            </div>
            <div>
              <Label className={labelCls}>Longitude</Label>
              <Input type="number" step="any" value={lng ?? ''} onChange={e => onChange?.({ lat, lng: parseFloat(e.target.value) || null })} className={`mt-1 ${inputCls}`} data-testid="map-lng-input" />
            </div>
          </div>
          {lat != null && lng != null && (
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs font-medium text-[#1E9BD9] hover:underline"
            >
              <MapPin className="h-3.5 w-3.5" /> {isAr ? 'عرض على Google Maps' : 'Open in Google Maps'}
            </a>
          )}
        </div>
      )}
    </div>
  );
}

const DARK_MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#1b1f27' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#1b1f27' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#8b96a8' }] },
  { featureType: 'water', stylers: [{ color: '#0b1118' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#2b3240' }] },
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
];
